// /admin/roles/:roleId — v6 mockup data-view="role-detail".
//
// Shows: usage card, permission grid grouped by resource, rules-that-grant
// table. System roles (tenant_admin) render their reserved permissions
// with a "reserved" tag; the Edit button is disabled for tenant_admin.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { roleClient } from "@/data/clients/roleClient";
import { permissionClient } from "@/data/clients/permissionClient";
import { roleClass, scopeSummary } from "@/pages/admin/people/format";
import { useRole } from "@/role";
import {
  permissionDisplayName,
  type PermissionCatalog,
  type Role,
} from "@/types/role";
import type { Rule } from "@/types/rule";
import "../AdminShared.css";

interface RoleWithUsage extends Role {
  users_count?: number;
  rules_count?: number;
}

const RESERVED_ROLE_ID = "tenant_admin";

export function RoleDetailPage(): JSX.Element {
  const { roleId: rawRoleId } = useParams<{ roleId: string }>();
  const roleId = rawRoleId ?? "";
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const { currentRole } = useRole();

  const [role, setRole] = useState<RoleWithUsage | null>(null);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (roleId === "") return;
    setLoading(true);
    setNotFound(false);
    try {
      const [roleData, catalogData, rulesResp] = await Promise.all([
        roleClient.get(roleId).catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
        permissionClient.getCatalog(),
        roleClient.listRules(roleId).catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            return { items: [] as Rule[] };
          }
          throw err;
        }),
      ]);
      if (!roleData) {
        setNotFound(true);
        setRole(null);
      } else {
        setRole(roleData);
      }
      setCatalog(catalogData);
      setRules(rulesResp.items);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load role" });
      }
    } finally {
      setLoading(false);
    }
  }, [roleId, handleApiError]);

  useEffect(() => {
    if (currentRole === null) return;
    void load();
  }, [currentRole, load]);

  const isReserved = role?.role_id === RESERVED_ROLE_ID;

  // Distinct location count from rules — "All N locations covered" when any
  // rule has location_scope.mode === "ALL"; otherwise count distinct ids.
  const locationsCovered = useMemo(() => {
    if (rules.length === 0) return 0;
    if (rules.some((r) => r.location_scope.mode === "ALL")) return -1; // -1 means "All"
    const ids = new Set<string>();
    for (const r of rules) {
      for (const id of r.location_scope.ids) ids.add(id);
    }
    return ids.size;
  }, [rules]);

  const totalUsersFromRules = useMemo(() => {
    // Approximate "Affects" — sum users per rule when present.
    return rules.reduce(
      (acc, _r) => acc, // ruleClient.list doesn't surface users_count today
      0,
    );
  }, [rules]);

  const handleDuplicate = useCallback(() => {
    if (!role) return;
    navigate(`/admin/roles/new?duplicate=${encodeURIComponent(role.role_id)}`);
  }, [role, navigate]);

  if (loading) {
    return (
      <section className="admin-page">
        <div className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/roles">← Roles</Link>
          </div>
          <div className="row">
            <h1>Loading…</h1>
          </div>
        </div>
      </section>
    );
  }

  if (notFound || !role) {
    return (
      <section className="admin-page">
        <div className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/roles">← Roles</Link>
          </div>
          <div className="row">
            <h1>Role not found</h1>
          </div>
          <p className="caption">
            No role with id <code>{roleId}</code> exists. <Link to="/admin/roles">Back to Roles</Link>
          </p>
        </div>
      </section>
    );
  }

  const heldSet = new Set(role.permission_set);
  const totalCatalogPerms =
    catalog?.groups.reduce((n, g) => n + g.permissions.length, 0) ?? 0;
  const usersCount = role.users_count ?? 0;
  const rulesCount = role.rules_count ?? rules.length;

  return (
    <section className="admin-page">
      <div className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/roles">← Roles</Link>
        </div>
        <div className="row">
          <h1>
            <span
              className={`v6-role-chip ${roleClass(role.role_id)}`}
              style={{ fontSize: 13, verticalAlign: "middle", marginRight: 10 }}
            >
              {role.role_id.replace(/_/g, "-").toUpperCase()}
            </span>
            {role.display_name}
          </h1>
          <div className="actions">
            <button
              type="button"
              className="v6-btn ghost sm"
              onClick={handleDuplicate}
            >
              Duplicate
            </button>
            <button
              type="button"
              className="v6-btn secondary sm"
              disabled={isReserved}
              title={isReserved ? "tenant_admin can't be edited" : undefined}
              onClick={() => {
                if (isReserved) return;
                navigate(`/admin/roles/${encodeURIComponent(role.role_id)}/edit`);
              }}
            >
              Edit
            </button>
          </div>
        </div>
        <div className="caption">
          <span
            className={`v6-type-chip ${role.is_system ? "system" : "custom"}`}
            style={{ marginRight: 8 }}
          >
            {role.is_system ? "System role" : "Custom role"}
          </span>
          {role.description}
        </div>
      </div>

      {/* Usage card — answers "is anything depending on this role?" */}
      <div className="v6-role-usage-card">
        <div className="usage-stat">
          <div className="usage-num">{usersCount}</div>
          <div className="usage-lbl">
            user{usersCount === 1 ? "" : "s"} hold this role
          </div>
        </div>
        <div className="usage-divider" />
        <div className="usage-stat">
          <div className="usage-num">{rulesCount}</div>
          <div className="usage-lbl">
            rule{rulesCount === 1 ? "" : "s"} grant{rulesCount === 1 ? "s" : ""} it
          </div>
        </div>
        <div className="usage-divider" />
        <div className="usage-stat">
          <div className="usage-num">
            {locationsCovered === -1 ? "All" : locationsCovered}
          </div>
          <div className="usage-lbl">locations covered</div>
        </div>
        <Link to="/admin/people" className="usage-link">
          See where →
        </Link>
      </div>

      <div className="v6-section-head">
        <span>What this role can do</span>
        <span className="sub">
          {role.permission_set.length} of {totalCatalogPerms} permissions
        </span>
      </div>

      <div className="v6-perm-grid">
        {catalog?.groups.map((group) => {
          // For tenant_admin we render every permission in the group as
          // held — including reserved ones — and add a "reserved" tag.
          // For other roles we only render the permissions the role
          // actually holds.
          const groupPermsToRender = isReserved
            ? group.permissions
            : group.permissions.filter((p) => heldSet.has(p.key));
          if (groupPermsToRender.length === 0) return null;
          const heldInGroup = group.permissions.filter((p) =>
            heldSet.has(p.key),
          ).length;
          return (
            <div className="v6-perm-group" key={group.key}>
              <div className="v6-perm-group-head">
                <span className="v6-perm-group-title">{group.label}</span>
                <span className="v6-perm-group-count">
                  {heldInGroup} of {group.permissions.length}
                </span>
              </div>
              {groupPermsToRender.map((perm) => (
                <div className="v6-perm-row held" key={perm.key}>
                  <span className="perm-check">✓</span>
                  <div>
                    <div className="perm-name">
                      {permissionDisplayName(perm)}
                      {perm.is_reserved ? (
                        <span className="perm-tag">reserved</span>
                      ) : null}
                    </div>
                    <div className="perm-key">{perm.key}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="v6-section-head" style={{ marginTop: 32 }}>
        <span>Rules that grant this role</span>
        <span className="sub">
          {rulesCount} rule{rulesCount === 1 ? "" : "s"}
          {totalUsersFromRules > 0 ? ` · affects ${totalUsersFromRules} users` : ""}
        </span>
      </div>

      {rules.length === 0 ? (
        <p
          style={{
            padding: "16px 18px",
            background: "var(--ring-tertiary-bg)",
            borderRadius: 8,
            color: "var(--ring-neutral-300)",
            fontSize: 13,
          }}
        >
          No rules grant this role yet. Create one from{" "}
          <Link to="/admin/rules/new">Rules → New rule</Link>.
        </p>
      ) : (
        <table className="v6-table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Group</th>
              <th>Scope</th>
              <th>Affects</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr
                key={rule.rule_id}
                className="clickable"
                onClick={() => {
                  const groupName = rule.group_match?.group_name;
                  if (!groupName) return; // group-less rule — nothing to drill into
                  navigate(
                    `/admin/people/groups/${encodeURIComponent(groupName)}`,
                  );
                }}
              >
                <td>
                  <b>{rule.display_name}</b>
                </td>
                <td>{rule.group_match?.group_name ?? "—"}</td>
                <td
                  style={{ fontSize: 13, color: "var(--ring-neutral-300)" }}
                >
                  {scopeSummary(rule.location_scope, 0)}
                </td>
                <td>—</td>
                <td className="row-actions">
                  <span
                    style={{ fontSize: 12, color: "var(--ring-neutral-300)" }}
                  >
                    Open →
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
